// NOTE: Keep these types in sync with the corresponding definitions in assessment/types.py.
//
// TODO: extend the generate_client_sources management command to automatically generate TS types
// from python sources, then create this file by feeding assessment/types.py into it.

import { CSVHeaderMap, CSVReducedHeaderMap } from "utils/reporting";

export enum AssessmentCategory {
  // TODO: the client and server disagree about the definition of this type. Where the client defines the
  // options to be "assignments", "activities", and "prepwork", the server defines them to be "assignment"
  // (note singular), "lesson", and "prep". We should standardize on one or the other.
  prepwork = "prepwork",
  activities = "activities",
  assignments = "assignments",
}

export type SectionScoreTableRow = {
  student_id: string;
  student_name: string;
  activities_score: string | null;
  assignments_score: string | null;
  prep_work_score: string | null;
  total_section_score: string;
};

export enum SectionScoreReportHeader {
  activities_score = "activities_score",
  assessed_pages_count = "assessed_pages_count",
  assignments_score = "assignments_score",
  course_code = "course_code",
  email = "email",
  external_id = "external_id",
  prep_score = "prep_score",
  section_external_id = "section_external_id",
  section_score_percent = "section_score_percent",
  section_score_rubric_4 = "section_score_rubric_4",
  section_score_rubric_5 = "section_score_rubric_5",
  section_title = "section_title",
  student_name = "student_name",
  term_title = "term_title",
}

/**
 * Translate from the symbolic table headers emitted by the server into the corresponding
 * translation strings for the full SectionScore report rendered on the admin dashboard.
 */
export const SECTION_SCORE_REPORT_FULL_HEADER_MAP: CSVHeaderMap<SectionScoreReportHeader> = {
  activities_score: "student_section_scores_report.columns.in_class_work_score",
  assessed_pages_count: "student_section_scores_report.columns.assessed_pages",
  assignments_score: "student_section_scores_report.columns.assignment_score",
  course_code: "student_section_scores_report.columns.course",
  email: "student_section_scores_report.columns.student_email",
  external_id: "student_section_scores_report.columns.student_external_id",
  prep_score: "student_section_scores_report.columns.prep_work_score",
  section_external_id: "student_section_scores_report.columns.section_external_id",
  section_score_percent: "assessment_dashboard.student_section_scores.section_score_percent",
  section_score_rubric_4: "assessment_dashboard.student_section_scores.section_score_scale_4",
  section_score_rubric_5: "assessment_dashboard.student_section_scores.section_score_scale_5",
  section_title: "student_section_scores_report.columns.section_title",
  student_name: "student_section_scores_report.columns.student_name",
  term_title: "student_section_scores_report.columns.term",
};

/**
 * Translate from the symbolic table headers emitted by the server into the corresponding
 * translation strings for the reduced SectionScore report rendered on the section dashboard.
 */
export const SECTION_SCORE_REPORT_REDUCED_HEADER_MAP: CSVReducedHeaderMap<SectionScoreReportHeader> =
  {
    student_name: "assessment_dashboard.all.student_name_header_title",
    prep_score: "assessment_dashboard.grades.prepwork",
    activities_score: "assessment_dashboard.grades.activities",
    assignments_score: "assessment_dashboard.grades.assignments",
    section_score_percent: "assessment_dashboard.student_section_scores.section_score_percent",
    section_score_rubric_4: "assessment_dashboard.student_section_scores.section_score_scale_4",
    section_score_rubric_5: "assessment_dashboard.student_section_scores.section_score_scale_5",
  };

export enum AssessmentPageType {
  assignment = "assignment",
  prep_work = "prep_work",
  activities = "activities",
  other = "other",
}

export enum AssessmentPublishingStatus {
  published = "published",
  draft = "draft",
}

export type AllAssessmentTableRow = {
  page_id: string;
  title: string;
  topic_title: string | null;
  status: string;
  unassessed_late_count: number;
  assessed_submissions_count: number;
  total_submissions_count: number;
  is_marked_complete: boolean;
  is_assessment_complete: boolean;
  publishing: AssessmentPublishingStatus;
  assessed_count: number;
  page_type: AssessmentPageType;
  avg_points: string;
  outcomes_count: number;
  outcomes_tooltip: string;
  last_submitted_date: string | null;
  hidden: boolean;
  due_at: string | null;
  submissions_last_collected_at: string | null;
};

export enum AllAssessmentReportHeader {
  term_title = "term_title",
  course_code = "course_code",
  section_title = "section_title",
  page_topic = "page_topic",
  page = "page",
  status = "status",
  publishing = "publishing",
  assessed = "assessed",
  page_type = "page_type",
  avg_points = "avg_points",
  outcomes = "outcomes",
  last_submitted = "last_submitted",
}

export const ALL_ASSESSMENT_REPORT_FULL_HEADER_MAP: CSVHeaderMap<AllAssessmentReportHeader> = {
  term_title: "student_section_scores_report.columns.term",
  course_code: "student_section_scores_report.columns.course",
  section_title: "student_section_scores_report.columns.section_title",
  page_topic: "assessment_dashboard.all.page_topic_header_title",
  page: "assessment_dashboard.all.page_header_title",
  status: "assessment_dashboard.all.status_header_title",
  publishing: "assessment_dashboard.all.publishing_header_title",
  assessed: "assessment_dashboard.all.assessed_header_title",
  page_type: "assessment_dashboard.all.page_type_header_title",
  avg_points: "assessment_dashboard.all.avg_points_header_title",
  outcomes: "assessment_dashboard.all.outcomes_header_title",
  last_submitted: "assessment_dashboard.all.last_submitted_header_title",
};

export const ALL_ASSESSMENT_REPORT_REDUCED_HEADER_MAP: CSVReducedHeaderMap<AllAssessmentReportHeader> =
  {
    page_topic: "assessment_dashboard.all.page_topic_header_title",
    page: "assessment_dashboard.all.page_header_title",
    status: "assessment_dashboard.all.status_header_title",
    publishing: "assessment_dashboard.all.publishing_header_title",
    assessed: "assessment_dashboard.all.assessed_header_title",
    page_type: "assessment_dashboard.all.page_type_header_title",
    avg_points: "assessment_dashboard.all.avg_points_header_title",
    outcomes: "assessment_dashboard.all.outcomes_header_title",
    last_submitted: "assessment_dashboard.all.last_submitted_header_title",
  };
